import { col, type Shape } from './base.ts';

export const OPPORTUNITY_RECORD: Shape = [
  col('summary', 'unitless', 'Short one-line summary of the opportunity.'),
  col('description', 'unitless', 'Full free-text description of the opportunity.'),
  col('type', 'unitless', 'Opportunity type / classification (e.g. intervention, optimization).'),
  col('source', 'unitless', 'System or workflow that raised the opportunity.'),
  col('status', 'unitless', 'Lifecycle status (e.g. open, planned, executed, closed).'),
  col('wellName', 'unitless', 'Name of the well the opportunity applies to.'),
  col('wellType', 'unitless', 'Type of the well (e.g. producer, injector).'),
  col('owner', 'unitless', 'Person or team accountable for the opportunity.'),
  col('interventionActivity', 'unitless', 'Planned intervention activity addressing the opportunity.'),
  col('ticketExternalID', 'unitless', 'External id of the linked tracking ticket.'),
  col('baselineOilRate', 'liquidRate', 'Baseline (pre-opportunity) oil production rate.'),
  col('baselineWaterRate', 'liquidRate', 'Baseline (pre-opportunity) water production rate.'),
  col('baselineGasRate', 'gasRate', 'Baseline (pre-opportunity) gas production rate.'),
  col('estimatedOilRate', 'liquidRate', 'Estimated (post-opportunity) oil production rate.'),
  col('estimatedWaterRate', 'liquidRate', 'Estimated (post-opportunity) water production rate.'),
  col('estimatedGasRate', 'gasRate', 'Estimated (post-opportunity) gas production rate.'),
  col('cumulativeOilGain', 'liquidVolume', 'Cumulative realized oil gain attributed to the opportunity.'),
  col('createdDate', 'unitless', 'Date the opportunity was created.'),
  col('plannedExecutionDate', 'unitless', 'Planned execution date for the opportunity.'),
  col('actualExecutionDate', 'unitless', 'Actual execution date of the opportunity.'),
  col('validationStatus', 'unitless', 'Validation status of the opportunity (e.g. validated, rejected).'),
  col('validationComments', 'unitless', 'Reviewer comments from validation.'),
  col('valueTrackingStatus', 'unitless', 'Value-tracking status (e.g. tracking, ended).'),
  col('valueTrackingEndDate', 'unitless', 'Date value tracking ended for the opportunity.'),
  col('valueTrackingEndReason', 'unitless', 'Reason value tracking ended.'),
];

/**
 * The shared value-tracking context scalars on `ValueTracking_Agent`:
 * the well name plus the source/status of the tracking record. Bare canonical
 * source columns (`suffix === canonical`, `prefix:''`). Kept separate from the
 * estimate/realized halves so the one source's context columns are named once.
 * The well-grain ref (`wellString`) is kept via `STRING_RELATION`, not here.
 */
export const VALUE_TRACKING_CONTEXT: Shape = [
  col('wellName', 'unitless', 'Name of the well the value-tracking record applies to.'),
  col('source', 'unitless', 'System or workflow that produced the value-tracking record.'),
  col('status', 'unitless', 'Value-tracking record status.'),
];

export const VALUE_ESTIMATES: Shape = [
  col('controlParameter', 'unitless', 'Optimized control parameter (e.g. choke, gas-lift rate).'),
  col('controlParameterScenarioName', 'unitless', 'Optimization scenario the control parameter comes from.'),
  col('controlParameterPostOptimizedValue', 'unitless', 'Post-optimization value of the control parameter.'),
  col('startDate', 'unitless', 'Start of the value-tracking window.'),
  col('endDate', 'unitless', 'End of the value-tracking window.'),
  col('oilPrice', 'pricePerBarrel', 'Oil price used to value realized gains.'),
  col('estimatedOilRate', 'liquidRate', 'Estimated post-optimization oil rate.'),
  col('estimatedGasRate', 'gasRate', 'Estimated post-optimization gas rate.'),
  col('estimatedTargetOilGain', 'liquidVolume', 'Estimated target oil gain from the optimization.'),
  col('estimatedTargetGasGain', 'gasVolumeModel', 'Estimated target gas gain from the optimization.'),
  col('estimatedTargetTotalGain', 'unitless', 'Estimated target total gain.'),
];

export const REALIZED_VALUE_GAINS: Shape = [
  col('realizedOilRateGain', 'liquidRate', 'Realized oil rate gain over the tracking window.'),
  col('realizedGasRateGain', 'gasRate', 'Realized gas rate gain over the tracking window.'),
  col('realizedWaterRateGain', 'liquidRate', 'Realized water rate gain over the tracking window.'),
  col('realizedOilVolumeGain', 'liquidVolume', 'Realized oil volume gain.'),
  col('realizedGasVolumeGain', 'gasVolume', 'Realized gas volume gain.'),
  col('realizedCumOilVolumeGain', 'liquidVolume', 'Realized cumulative oil volume gain.'),
  col('realizedCumGasVolumeGain', 'gasVolume', 'Realized cumulative gas volume gain.'),
  col('realizedCumGasVolumeGainBOE', 'liquidVolume', 'Realized cumulative gas volume gain, BOE-equivalent.'),
  col('realizedTotalVolumeGainBOE', 'liquidVolume', 'Realized total volume gain, BOE-equivalent.'),
  col('realizedTotalCumVolumeGainBOE', 'liquidVolume', 'Realized cumulative total volume gain, BOE-equivalent.'),
  col('realizedOilPerformance', 'unitless', 'Realized oil performance ratio vs target.'),
  col('realizedGasPerformance', 'unitless', 'Realized gas performance ratio vs target.'),
  col('realizedWaterPerformance', 'unitless', 'Realized water performance ratio vs target.'),
  col('realizedDollarValueGain', 'money', 'Realized dollar value gain.'),
  col('realizedDollarCumValueGain', 'money', 'Realized cumulative dollar value gain.'),
  col('wellUptimeHours', 'unitless', 'Well uptime hours over the tracking window.'),
];
/**
 * Shared per-string sensor cluster common to both `ESPMeasurements`
 * and `GasLiftMeasurements`: the wellbore / choke / flowline real-time
 * readings plus the artificial-lift type, identical (un-prefixed) on both
 * sources. Composed by spread into `ESP_MEASUREMENTS` / `GASLIFT_MEASUREMENTS`
 * alongside each source's lift-specific extras. Bare canonical columns
 * (`suffix === canonical`, `prefix:''`); each is a TimeSeries reference.
 */
